import { Button, Empty, Form, Input, InputNumber, Select, Space, Switch, Typography } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COMPONENT_CONSTRAINTS, propTypeRequiresOptions } from '../constants';
import type { PropFieldModel } from '../types';
import { slugifyKey } from '../utils/schema';

const { Text } = Typography;

interface PropPropertiesPanelProps {
  prop: PropFieldModel | null;
  onChange: (patch: Partial<PropFieldModel>) => void;
  disabled?: boolean;
}

/** Sağ panel — seçili prop'un özellikleri (label/key/required/options/default). */
export function PropPropertiesPanel({ prop, onChange, disabled }: PropPropertiesPanelProps) {
  const { t } = useTranslation('component-studio');

  if (!prop) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('builder.selectPropHint')}
        style={{ marginTop: 24 }}
      />
    );
  }

  // Etiket değişince key boşsa otomatik türet (kullanıcı özelleştirmişse dokunma).
  const handleLabelChange = (label: string) => {
    const patch: Partial<PropFieldModel> = { label };
    if (prop.key.trim().length === 0) patch.key = slugifyKey(label);
    onChange(patch);
  };

  const updateOption = (index: number, value: string) =>
    onChange({ options: prop.options.map((o, i) => (i === index ? value : o)) });
  const addOption = () => onChange({ options: [...prop.options, ''] });
  const removeOption = (index: number) =>
    onChange({ options: prop.options.filter((_, i) => i !== index) });

  const renderDefault = () => {
    if (prop.type === 'boolean') {
      return (
        <Space>
          <Switch
            checked={prop.default.toLowerCase() === 'true'}
            onChange={(v) => onChange({ default: v ? 'true' : 'false' })}
            data-testid="prop-default-boolean"
          />
          <Text type="secondary">{prop.default.toLowerCase() === 'true' ? t('builder.propType.boolean') : '—'}</Text>
        </Space>
      );
    }
    if (prop.type === 'select') {
      return (
        <Select
          allowClear
          style={{ width: '100%' }}
          value={prop.default || undefined}
          onChange={(v) => onChange({ default: v ?? '' })}
          options={prop.options.filter((o) => o.trim()).map((o) => ({ value: o, label: o }))}
          data-testid="prop-default-select"
        />
      );
    }
    if (prop.type === 'number') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          value={prop.default === '' ? undefined : Number(prop.default)}
          onChange={(v) => onChange({ default: v == null ? '' : String(v) })}
          data-testid="prop-default-number"
        />
      );
    }
    return (
      <Input
        value={prop.default}
        onChange={(e) => onChange({ default: e.target.value })}
        data-testid="prop-default-text"
      />
    );
  };

  return (
    <div>
      <Text strong>{t('builder.properties')}</Text>
      <Form layout="vertical" style={{ marginTop: 12 }} disabled={disabled}>
        <Form.Item label={t('builder.prop.label')} required style={{ marginBottom: 12 }}>
          <Input
            value={prop.label}
            maxLength={COMPONENT_CONSTRAINTS.maxLabelLength}
            onChange={(e) => handleLabelChange(e.target.value)}
            data-testid="prop-prop-label"
          />
        </Form.Item>

        <Form.Item
          label={t('builder.prop.key')}
          required
          help={t('builder.prop.keyHint')}
          style={{ marginBottom: 12 }}
        >
          <Input
            value={prop.key}
            maxLength={COMPONENT_CONSTRAINTS.maxKeyLength}
            onChange={(e) => onChange({ key: e.target.value })}
            data-testid="prop-prop-key"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 12 }}>
          <Space>
            <Switch
              checked={prop.required}
              onChange={(required) => onChange({ required })}
              data-testid="prop-prop-required"
            />
            <Text>{t('builder.prop.required')}</Text>
          </Space>
        </Form.Item>

        {propTypeRequiresOptions(prop.type) ? (
          <Form.Item label={t('builder.prop.options')} required style={{ marginBottom: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              {prop.options.map((opt, index) => (
                <Space.Compact key={index} style={{ width: '100%' }}>
                  <Input
                    value={opt}
                    placeholder={t('builder.prop.optionPlaceholder')}
                    onChange={(e) => updateOption(index, e.target.value)}
                    data-testid="prop-prop-option"
                  />
                  <Button
                    danger
                    icon={<Trash2 size={14} />}
                    aria-label={t('builder.prop.remove')}
                    onClick={() => removeOption(index)}
                  />
                </Space.Compact>
              ))}
              <Button
                type="dashed"
                icon={<Plus size={14} />}
                onClick={addOption}
                disabled={prop.options.length >= COMPONENT_CONSTRAINTS.maxOptionsPerField}
                data-testid="prop-prop-add-option"
              >
                {t('builder.prop.addOption')}
              </Button>
            </Space>
          </Form.Item>
        ) : null}

        <Form.Item
          label={t('builder.prop.default')}
          help={t('builder.prop.defaultHint')}
          style={{ marginBottom: 12 }}
        >
          {renderDefault()}
        </Form.Item>
      </Form>
    </div>
  );
}
